import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, ArrowRight, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { usePayment } from '@/context/PaymentContext';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl } from '@/lib/api';

const API_BASE_URL = getApiUrl();

export function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = usePayment();
  const { login } = useAuth();
  
  const [countdown, setCountdown] = useState(5);
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSet, setPasswordSet] = useState(false);

  // Verify Stripe subscription on page load
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // Verify the subscription with backend (public endpoint)
      fetch(`${API_BASE_URL}/api/verify-subscription-public?session_id=${sessionId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setVerified(true);
            setUserEmail(data.email || '');
            setNeedsPassword(data.needsPasswordSetup || false);
            
            // If user has password, auto-login
            if (!data.needsPasswordSetup && data.email) {
              // User has password, they can login normally later
            }
          }
        })
        .catch(err => console.error('Verification error:', err))
        .finally(() => setVerifying(false));
    } else {
      setVerifying(false);
    }
  }, [searchParams]);

  // Auto-redirect after successful payment (only if password is set)
  useEffect(() => {
    if (!verified || verifying || needsPassword || passwordSet) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/search');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [verified, verifying, needsPassword, passwordSet, navigate]);

  const handleSetPassword = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsSettingPassword(true);
    setPasswordError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/set-password-from-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: searchParams.get('session_id'),
          password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setPasswordError(data.error || 'Failed to set password');
        setIsSettingPassword(false);
        return;
      }

      // Auto-login with new credentials
      if (data.accessToken && userEmail) {
        await login(userEmail, password);
      }

      setPasswordSet(true);
      setTimeout(() => navigate('/search'), 1500);
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to set password');
      setIsSettingPassword(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Loader2 size={48} className="animate-spin mx-auto mb-4 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verifying Payment...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your subscription.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!verified) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-amber-600 text-2xl">!</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verification Failed
            </h1>
            <p className="text-gray-600 mb-6">
              We could not verify your payment. Please contact support.
            </p>
            <Button
              onClick={() => navigate('/search')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Back to Search
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show password setup form for new users
  if (needsPassword && !passwordSet) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to RaiseBeacon!
            </h1>
            <p className="text-gray-600 mb-2">
              Your subscription is active.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-blue-800 mb-2">
                <Mail className="w-4 h-4" />
                <span className="font-medium text-sm">Set your password to continue</span>
              </div>
              <p className="text-blue-700 text-xs text-left">
                Email: <strong>{userEmail}</strong>
              </p>
            </div>

            <form onSubmit={handleSetPassword} className="space-y-4 text-left">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    minLength={6}
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {passwordError && (
                <p className="text-red-600 text-sm text-center">{passwordError}</p>
              )}

              <Button
                type="submit"
                disabled={isSettingPassword || password.length < 6 || password !== confirmPassword}
                className="w-full h-12 bg-gradient-to-r from-[#2B7FFF] to-[#1A5FCC] hover:from-[#1A5FCC] hover:to-[#2B7FFF] text-white"
              >
                {isSettingPassword ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Setting Password...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (passwordSet) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              All Set!
            </h1>
            <p className="text-gray-600">
              Your password has been set. Redirecting...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to RaiseBeacon.us!
            </h1>
            <p className="text-gray-600 mb-2">
              Your subscription is now active.
            </p>
            <div className="bg-green-50 text-green-700 text-sm py-2 px-4 rounded-lg mb-6 inline-block">
              Premium access unlocked
            </div>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
              <Loader2 size={16} className="animate-spin" />
              Redirecting in {countdown} seconds...
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/search')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Start Searching Investors
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

